import type {
  AccountPracInfoRequestDto,
  AccountPracInfoResponseDto,
  AccountPracInfoType,
} from '../../types/api';
import { BaseServiceClient } from '../BaseServiceClient';

// Which AccountPracInfoResponseDto keys a given request Type actually populates. A response
// fetched with a narrow Types list (e.g. ['AccountSettings', 'AccountSras']) still comes back
// through normalizeAccountPracInfoPayload with every OTHER key present as an explicit
// `undefined` own-property (see normalizeNestedAccountPracInfo below), so merging that whole
// dto onto a cached record with `{ ...cached, ...dto }` silently wipes any secondary data
// (Touchpoints/BillPayInfo/AdminOptions/SraList/SraUser) the cache already had for types that
// weren't part of THIS request. mergeAccountPracInfoByRequestedTypes only copies over the keys
// tied to the types actually requested, leaving everything else in the cache untouched.
const ACCOUNT_PRAC_INFO_TYPE_RESPONSE_KEYS: Record<AccountPracInfoType, Array<keyof AccountPracInfoResponseDto>> = {
  AccountSettings: ['AccountSettings', 'SraUser'],
  Subscriptions: ['BillPayInfo'],
  Touchpoints: ['Touchpoints'],
  AdminOptions: ['AdminOptions'],
  AccountSras: ['SraList'],
};

// AccountId/SiteList aren't gated behind any AccountPracInfoType — the backend includes them on
// every provider_account_get response regardless of what was requested — so they're always safe
// to take from the latest response.
const ACCOUNT_PRAC_INFO_ALWAYS_MERGED_KEYS: Array<keyof AccountPracInfoResponseDto> = ['AccountId', 'SiteList'];

/** Merges a freshly-fetched AccountPracInfoResponseDto onto a cached one, touching only the
 * fields that `requestedTypes` actually asked the backend for (plus the always-present
 * AccountId/SiteList). Everything else in `cached` is left exactly as it was. Pass this the same
 * `Types` array used for the request that produced `dto`. */
export const mergeAccountPracInfoByRequestedTypes = (
  cached: AccountPracInfoResponseDto | null | undefined,
  dto: AccountPracInfoResponseDto,
  requestedTypes: AccountPracInfoType[]
): AccountPracInfoResponseDto => {
  if (!cached) {
    return dto;
  }

  const merged = { ...cached } as any;

  for (const key of ACCOUNT_PRAC_INFO_ALWAYS_MERGED_KEYS) {
    if ((dto as any)[key] !== undefined) {
      merged[key] = (dto as any)[key];
    }
  }

  for (const type of requestedTypes) {
    for (const key of ACCOUNT_PRAC_INFO_TYPE_RESPONSE_KEYS[type] ?? []) {
      if ((dto as any)[key] !== undefined) {
        merged[key] = (dto as any)[key];
      }
    }
  }

  return merged as AccountPracInfoResponseDto;
};

interface AccountPracInfoServiceOptions {
  userStore?: any;
  storage?: any;
}

export const normalizeAccountPracInfoPayload = (payload: unknown): AccountPracInfoResponseDto => {
  const parseIfString = (value: unknown): unknown => {
    if (typeof value !== 'string') {
      return value;
    }

    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };

  const isMeaningfulValue = (value: unknown): boolean => {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length > 0 && trimmed !== 'null' && trimmed !== 'undefined';
    }

    return true;
  };

  const parseFieldIfString = <T = unknown>(value: T): T => {
    return parseIfString(value) as T;
  };

  const normalizeNestedAccountPracInfo = (value: AccountPracInfoResponseDto): AccountPracInfoResponseDto => {
    const next = { ...value } as any;

    next.AccountSettings = parseFieldIfString(next.AccountSettings);
    next.SraUser = parseFieldIfString(next.SraUser);
    next.SraList = parseFieldIfString(next.SraList);
    next.SiteList = parseFieldIfString(next.SiteList);
    next.Touchpoints = parseFieldIfString(next.Touchpoints);
    next.AdminOptions = parseFieldIfString(next.AdminOptions);
    next.BillPayInfo = parseFieldIfString(next.BillPayInfo);

    if (next.AccountSettings && typeof next.AccountSettings === 'object') {
      next.AccountSettings = {
        ...next.AccountSettings,
        PracInfo: parseFieldIfString(next.AccountSettings.PracInfo),
        Enables: parseFieldIfString(next.AccountSettings.Enables),
        UVars: parseFieldIfString(next.AccountSettings.UVars),
        PracPhi: parseFieldIfString(next.AccountSettings.PracPhi),
        Registries: parseFieldIfString(next.AccountSettings.Registries),
        XInfo: parseFieldIfString(next.AccountSettings.XInfo),
        PlaList: parseFieldIfString(next.AccountSettings.PlaList),
        AggReportDefs: parseFieldIfString(next.AccountSettings.AggReportDefs),
      };
      // Cleans out SkinDef from any account cached before the backend stopped sending it —
      // AccountSettingsDto no longer declares the field (see its own comment).
      delete next.AccountSettings.SkinDef;
    }

    return next as AccountPracInfoResponseDto;
  };

  const hasAccountPracInfoShape = (value: unknown): boolean => {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const candidate = value as Record<string, unknown>;
    return !!(
      candidate.AccountSettings
      || candidate.SraUser
      || candidate.SraList
      || candidate.SiteList
      || candidate.AccountId
    );
  };

  let current: unknown = payload;
  for (let i = 0; i < 10; i += 1) {
    current = parseIfString(current);

    if (hasAccountPracInfoShape(current)) {
      return normalizeNestedAccountPracInfo(current as AccountPracInfoResponseDto);
    }

    if (!current || typeof current !== 'object') {
      continue;
    }

    const envelope = current as Record<string, unknown>;
    const nextCandidates = [
      envelope.payload,
      envelope.Payload,
      envelope.result,
      envelope.Result,
      envelope.data,
      envelope.Data,
      envelope.dto,
      envelope.Dto,
    ];
    const next = nextCandidates.find((candidate) => isMeaningfulValue(candidate));

    if (!isMeaningfulValue(next) || next === current) {
      break;
    }

    current = next;
  }

  return normalizeNestedAccountPracInfo(parseIfString(current) as AccountPracInfoResponseDto);
};

export class AccountPracInfoService extends BaseServiceClient {
  constructor(apiService: any) {
    super(apiService);
  }

  async getAccountPracInfo(
    request: AccountPracInfoRequestDto,
    options?: AccountPracInfoServiceOptions
  ): Promise<AccountPracInfoResponseDto> {
    const response = await this.callService('provider_account_get', request, 'sync', {
      userStore: options?.userStore,
      storage: options?.storage,
    });

    const success = Boolean((response as any)?.success ?? (response as any)?.Success);
    if (!success) {
      throw new Error((response as any)?.message || (response as any)?.errorDescription || 'provider_account_get failed');
    }

    const payload =
      (response as any)?.payload
      ?? (response as any)?.Payload
      ?? (response as any)?.result
      ?? (response as any)?.Result
      ?? (response as any)?.data
      ?? (response as any)?.Data
      ?? response;
    return this.parsePayload(payload);
  }

  private parsePayload(payload: unknown): AccountPracInfoResponseDto {
    return normalizeAccountPracInfoPayload(payload);
  }
}
